/**
 * Filtersecties van de renovatieticketlijst.
 *
 * Gedeeld door beide weergaven ("Recent" en "Per project"): de filterbalk staat
 * op paginaniveau en de filters gaan naar dezelfde backend-parameters (zie
 * `utils/renoTicketParams.ts`).
 *
 * @module RenoHelpdesk/Configs/RenoFilterSections
 */

import type { FilterSection } from 'shared/components/filterBar/types';
import { getUserName } from 'shared/utils/users';
import {
  RenoMessageType,
  RenoTicketPriority,
  RenoTicketStatus,
} from '../types/renoTicket';

/** Filteropties die uit de backend komen (`GET /reno-helpdesk/filter-options`). */
export interface RenoFilterOptions {
  projects: { id: number; title: string }[];
  suppliers: { id: number; name: string }[];
  buildings: { id: string }[];
  units: { id: string; buildingId: string }[];
  messageTypes: string[];
  assignees: string[];
}

interface BuildRenoFilterSectionsArgs {
  activeFilters: Record<string, string[]>;
  filterOptions: RenoFilterOptions;
  activeUsers: any[];
  onUpdate: (key: string, values: string[]) => void;
}

export function buildRenoFilterSections({
  activeFilters,
  filterOptions,
  activeUsers,
  onUpdate,
}: BuildRenoFilterSectionsArgs): FilterSection[] {
  return [
    {
      key: 'status',
      label: 'Fase',
      options: Object.values(RenoTicketStatus)
        .filter((s) => s !== RenoTicketStatus.UNKNOWN)
        .map((s) => ({ key: s, label: s })),
      selectedValues: activeFilters.status ?? [],
      onUpdate: (v) => onUpdate('status', v),
      color: 'brick' as const,
    },
    {
      key: 'priority',
      label: 'Prioriteit',
      options: Object.values(RenoTicketPriority)
        .filter((p) => p !== RenoTicketPriority.UNKNOWN)
        .map((p) => ({ key: p, label: p })),
      selectedValues: activeFilters.priority ?? [],
      onUpdate: (v) => onUpdate('priority', v),
      color: 'yellow' as const,
    },
    {
      key: 'project',
      label: 'Project',
      options: filterOptions.projects.map((p) => ({ key: String(p.id), label: p.title })),
      selectedValues: activeFilters.project ?? [],
      onUpdate: (v) => onUpdate('project', v),
      color: 'moss' as const,
      singleSelect: true,
    },
    {
      key: 'supplier',
      label: 'Leverancier',
      options: filterOptions.suppliers.map((s) => ({ key: String(s.id), label: s.name })),
      selectedValues: activeFilters.supplier ?? [],
      onUpdate: (v) => onUpdate('supplier', v),
      color: 'neutral' as const,
      singleSelect: true,
    },
    {
      key: 'building',
      label: 'Gebouw',
      options: filterOptions.buildings.map((b) => ({ key: b.id, label: b.id })),
      selectedValues: activeFilters.building ?? [],
      onUpdate: (v) => onUpdate('building', v),
      color: 'neutral' as const,
      singleSelect: true,
    },
    {
      key: 'messageType',
      label: 'Type boodschap',
      options: Object.values(RenoMessageType).map((m) => ({ key: m, label: m })),
      selectedValues: activeFilters.messageType ?? [],
      onUpdate: (v) => onUpdate('messageType', v),
      color: 'yellow' as const,
    },
    {
      // Zelfde filter als op de helpdesk ("Email Status"): het rode bolletje op de
      // RS-/RT-knoppen zegt dát er een ongelezen mail is, maar zonder filter moet je
      // de hele lijst afspeuren om ze te vinden.
      key: 'emailStatus',
      label: 'E-mailstatus',
      options: [{ key: 'unread', label: 'Ongelezen berichten' }],
      selectedValues: activeFilters.emailStatus ?? [],
      onUpdate: (v) => onUpdate('emailStatus', v),
      color: 'moss' as const,
    },
    {
      key: 'assignee',
      label: 'Medewerker',
      options: filterOptions.assignees.map((id) => ({
        key: id,
        label: getUserName(id, activeUsers).name || id,
      })),
      selectedValues: activeFilters.assignee ?? [],
      onUpdate: (v) => onUpdate('assignee', v),
      color: 'neutral' as const,
      singleSelect: true,
    },
  ];
}
